import Dexie, { type EntityTable } from 'dexie';
import type { MetaEntry } from '../types/meta-entry';
import type { OutboxEntry } from '../types/outbox-entry';
import type { Rejection } from '../types/rejection';
import type { StoredBlob } from '../types/stored-blob';
import type { StoredRecord } from '../types/stored-record';

/**
 * The device's copy of everything, in IndexedDB: `records` (server state), `outbox` (pending
 * mutations, in order), `blobs` (file bytes by sha256), `rejections` and `meta` (cursor,
 * server_id, device_id).
 */
export class ReplicaDatabase extends Dexie {
  records!: EntityTable<StoredRecord, 'id'>;
  outbox!: EntityTable<OutboxEntry, 'seq'>;
  blobs!: EntityTable<StoredBlob, 'sha256'>;
  rejections!: EntityTable<Rejection, 'op_id'>;
  meta!: EntityTable<MetaEntry, 'key'>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      records: 'id, collection, rev',
      outbox: '++seq, &op_id, record_id, collection',
      blobs: 'sha256, uploaded',
      rejections: 'op_id, at',
      meta: 'key',
    });
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    const entry = await this.meta.get(key);
    // Values are written by setMeta with the same key, so the caller knows their type.
    return entry?.value as T | undefined;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await this.meta.put({ key, value });
  }
}
